/**
 * KPI Service
 * 
 * Service for fetching key performance indicator data for equipment
 * with caching mechanisms for improved performance.
 */

import { apiClient } from '../api';
import { KPIData, DateRange } from './types';

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Cache structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// In-memory cache
const kpiCache: Record<string, CacheEntry<KPIData>> = {};

/**
 * Generates a cache key for KPI data
 */
const getCacheKey = (site: string, apmId: string, dateRange: DateRange): string => {
  return `kpi_${site}_${apmId}_${dateRange.startDate}_${dateRange.endDate}`;
};

/**
 * Checks if a cache entry is still valid
 */
const isCacheValid = (entry: CacheEntry<any>): boolean => {
  return Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * KPI Service with methods for fetching KPI data
 */
export const kpiService = {
  /**
   * Fetches KPI data for a specific site and APM ID
   * with optional equipment ID for more specific data
   */
  getKPIData: async (site: string, apmId: string, dateRange: DateRange): Promise<KPIData> => {
    
    const cacheKey = getCacheKey(site, apmId, dateRange);
    
    // Check cache first
    const cachedData = kpiCache[cacheKey];
    if (cachedData && isCacheValid(cachedData)) {
      return cachedData.data;
    }
        
    const data = await apiClient.get<KPIData>(
      '/kpi',
      {
        params: {
          site,
          asset: encodeURIComponent(apmId),
          start_date: encodeURIComponent(dateRange.startDate),
          end_date: encodeURIComponent(dateRange.endDate)
        }
      }
    );
    
    // Update cache
    kpiCache[cacheKey] = {
      data,
      timestamp: Date.now()
    };
    
    return data;
  },
  
  /**
   * Clears the KPI cache for a specific site and APM ID
   * or the entire cache if no parameters are provided
   */
  clearCache: (site?: string, apmId?: string, dateRange?: DateRange): void => {
    if (site && apmId && dateRange) {
      const cacheKey = getCacheKey(site, apmId, dateRange);
      delete kpiCache[cacheKey];
    } else {
      // Clear all cache
      Object.keys(kpiCache).forEach(key => {
        delete kpiCache[key];
      });
    }
  }
};